import type { InboxV2Conversation } from "../types";

import {
  isInboxV2InternalLikeMessage,
  isInboxV2SystemMessage,
} from "./message-meta";

type ReplyRestrictionConversation = Pick<
  InboxV2Conversation,
  "lastMessageAt" | "messageType" | "platform"
> &
  Partial<Pick<InboxV2Conversation, "messages">>;

export function isInboxV2ConversationReplyExpired(
  conversation: ReplyRestrictionConversation
) {
  return Boolean(getInboxV2ConversationReplyRestriction(conversation));
}

export function getInboxV2ConversationReplyRestriction(
  conversation: ReplyRestrictionConversation
) {
  if (conversation.messageType !== "dm") {
    return;
  }

  if (conversation.platform === "tiktok") {
    return getInboxV2TikTokReplyRestriction(conversation.messages ?? []);
  }

  const latestSocialMessage = conversation.messages?.findLast(
    (message) =>
      !isInboxV2SystemMessage(message) && !isInboxV2InternalLikeMessage(message)
  );
  const lastMessageTime = new Date(
    latestSocialMessage?.createdAt ?? conversation.lastMessageAt
  ).getTime();

  if (Number.isNaN(lastMessageTime)) {
    return;
  }

  return Date.now() - lastMessageTime > 7 * 24 * 60 * 60 * 1000
    ? "This message is over 7 days old and can no longer be replied to. You can still make internal comments."
    : undefined;
}

function getInboxV2TikTokReplyRestriction(
  messages: InboxV2Conversation["messages"]
) {
  const socialMessages = messages
    .filter(
      (message) =>
        message.kind !== "message-creating" &&
        !isInboxV2SystemMessage(message) &&
        !isInboxV2InternalLikeMessage(message)
    )
    .map((message) => ({
      message,
      timestamp: new Date(message.createdAt).getTime(),
    }))
    .filter(({ timestamp }) => !Number.isNaN(timestamp))
    .toSorted((a, b) => a.timestamp - b.timestamp);
  const customerMessages = socialMessages.filter(
    ({ message }) => !message.isConversationAccountSender
  );
  const firstCustomerMessage = customerMessages[0];
  const latestCustomerMessage = customerMessages.at(-1);

  if (!firstCustomerMessage || !latestCustomerMessage) {
    return;
  }

  const customerReplyIndex = socialMessages.indexOf(latestCustomerMessage);
  const customerHasRepliedToBusiness = socialMessages
    .slice(0, customerReplyIndex)
    .some(({ message }) => message.isConversationAccountSender);
  const windowStart = customerHasRepliedToBusiness
    ? latestCustomerMessage.timestamp
    : firstCustomerMessage.timestamp;
  const windowEnd = windowStart + 48 * 60 * 60 * 1000;

  if (Date.now() <= windowEnd) {
    if (customerHasRepliedToBusiness) {
      return;
    }

    const initialBusinessReplyCount = socialMessages.filter(
      ({ message, timestamp }) =>
        message.isConversationAccountSender && timestamp >= windowStart
    ).length;

    return initialBusinessReplyCount >= 10
      ? "TikTok allows up to 10 replies within 48 hours of the customer's first message. This limit has been reached. You can reply again if the customer responds, or send up to three additional messages after the 48-hour window ends."
      : undefined;
  }

  const inactiveBusinessReplyCount = socialMessages.filter(
    ({ message, timestamp }) =>
      message.isConversationAccountSender && timestamp > windowEnd
  ).length;

  return inactiveBusinessReplyCount >= 3
    ? "TikTok allows up to three additional messages after the 48-hour reply window. This limit has been reached; the customer must reply before you can send another message."
    : undefined;
}
